import {Injectable, Injector} from "@angular/core";
import {
    collection,
    component,
    EntityData,
    EntityModel,
    FedeltaImageData,
    field,
    identifier,
    ImageUtils,
    transientField
} from "@fedelta-pos/data-client";

export enum AppLoginIdentificationMethod {
    EMAIL = <any>"email",
    MOBILE = <any>"mobile",
    MEMBER_NO = <any>"memberno",
    MEMBER_NO_PLUS_MOBILE = <any>"membernoplusmobile"
}

export enum AppLoginAuthenticationMethod {
    // noinspection JSUnusedGlobalSymbols
    NONE = <any>"none",
    PASSWORD = <any>"password",
    DOB = <any>"dob",
    CODE = <any>"code"
}

export enum AppDealActionStyle {
    INLINE = <any>"inline",
    CORNER = <any>"corner",
    PEEL = <any>"peel"
}

export enum AppPromoTextPosition {
    TOP = <any>"top",
    CENTER = <any>"center",
    BOTTOM = <any>"bottom"
}

export enum AppHeaderPlacement {
    FULL_WIDTH = <any>"FULL_WIDTH",
    CONTENT = <any>"CONTENT"
}

export enum AppNavbarPlacement {
    PAGE_HEADER = <any>"PAGE_HEADER",
    SIDEBAR = <any>"SIDEBAR"
}

export enum TableLookupMethod {
    MANUAL = <any>"Manual",
    QR_CODE = <any>"QR Code"
}

export enum ButtonShape {
    SQUARE = <any>"SQUARE",
    ROUNDED = <any>"ROUNDED",
    OVAL = <any>"OVAL"
}

export enum ButtonStyle {
    FLAT = <any>"FLAT",
    OUTLINE = <any>"OUTLINE",
    RAISED = <any>"RAISED"
}

export class AppLoginOptionsData extends EntityData<AppLoginOptionsData> {
    @field showCarousel: boolean;
    @field allowMemberLogin: boolean;
    @field allowGuestOrdering: boolean;
}

export class AppFeedbackData extends EntityData<AppFeedbackData> {
    @field categoryName: string;
}

export class ColorPairData extends EntityData<ColorPairData> {
    @field foregroundColor: string;
    @field backgroundColor: string;
}

export class AppThemePaletteEntryData extends EntityData<AppThemePaletteEntryData> {
    @field name: string;
    @component colorPair: ColorPairData;
}

export class MessageData extends EntityData<AppThemePaletteEntryData> {
    @field id: number;
}

export class AppCardThemeData extends EntityData<AppCardThemeData> {
    @field name: string;
    @field widthPx: number;
    @field heightPx: number;
    @field bodyPadding: string;
    @field backgroundColor: string;
    @field maxColumnSpec: string;
    @field headerTextColor: string;
    @field headerBackgroundColor: string;
    @field headerBackgroundOpacity: number;
    @field footerTextColor: string;
    @field footerBackgroundColor: string;
    @field footerBackgroundOpacity: number;
    @field placeholderBackgroundColor: string;
    @field placeholderBorderColor: string;
    @field placeholderOpacity: number;
    @field stampColor: string;
    @field stampOpacity: number;
    @field svgStampIcon: string;
    @field stampIconScale: number;
    @field stampIconRotateFactor: number;
}

export class AppCustomTextData extends EntityData<AppCustomTextData> {
    @identifier id: number;
    @field key: string;
    @field value: string;
}

export class AppThemeSettingValueData extends EntityData<AppThemeSettingValueData> {
    @identifier id: number;
    @field key: string;
    @field value: string;
}

export class AppSettingData extends EntityData<AppSettingData> {
    @identifier id: number;
    @field name: string;
    @field value: string;
}

export class AppData extends EntityData<AppData> {
    @identifier id: number;
    @field appName: string;
    @field shortAppName: string;
    @field appLiveMode: boolean = true;
    @field defaultMenuId: number;
    @field stripeTestPublicKey: string;
    @field googlePayMerchantName: string;
    @field googlePayMerchantId: string;
    @field integratedLoyalty: boolean = false;
    @field loginIdentificationMethod: AppLoginIdentificationMethod;
    @field loginAuthenticationMethod: AppLoginAuthenticationMethod;
    @field supportsRegistration: boolean;
    @field captureMemberDOB: boolean;
    @field supportsForgotPassword: boolean;
    @field supportsRedemption: boolean;
    @field memberNoIsNumeric: boolean = false;
    @field signupDelayMillis: number;
    @field defaultImage: string;
    @field dealActionStyle: AppDealActionStyle = AppDealActionStyle.PEEL;
    @field promoTextPosition: AppPromoTextPosition = AppPromoTextPosition.TOP;
    @field showDesktopMenuPromos: boolean;
    @field showMobileMenuPromos: boolean;
    @field storeRange: number;
    @field storeSearchRange: number;
    @field storeLimit: number;
    @field showStoreImagesOnStoreSelection: boolean = false;
    @field showStoreImagesOnMobileStoreSelection: boolean = false;
    @field deliveryStoreRange: number;
    @field deliveryStoreLimit: number;
    @field deliveryDistanceByRoad: boolean;
    @field headerPlacement: AppHeaderPlacement = AppHeaderPlacement.CONTENT;
    @field navbarPlacement: AppNavbarPlacement = AppNavbarPlacement.SIDEBAR;
    @field showDesktopHeaderImage: boolean = false;
    @field showDesktopHeaderLogo: boolean = true;
    @field showDesktopHeaderVerticalLogo: boolean = false;
    @field showMobileHeaderVerticalLogo: boolean = false;
    @field tableLookupMethod: TableLookupMethod = TableLookupMethod.MANUAL;
    @field onlineManualTableLookup: boolean = true;
    @field tableOrderingName: string;
    @field showPhoneNumberForPickup: boolean = false;
    @field showPhoneNumberForDelivery: boolean = true;
    @field showPhoneNumberForTable: boolean = false;
    @field requirePhoneNumberForPickup: boolean = false;
    @field requirePhoneNumberForDelivery: boolean = true;
    @field requirePhoneNumberForTable: boolean = false;
    @field showEmailForPickup: boolean = true;
    @field showEmailForDelivery: boolean = true;
    @field showEmailForTable: boolean = false;
    @field requireEmailForPickup: boolean = false;
    @field requireEmailForDelivery: boolean = false;
    @field requireEmailForTable: boolean = false;
    @field showNameForPickup: boolean = true;
    @field showNameForDelivery: boolean = true;
    @field showNameForTable: boolean = false;
    @field requireFirstNameForPickup: boolean = true;
    @field requireFirstNameForDelivery: boolean = true;
    @field requireFirstNameForTable: boolean = false;
    @field showStoreAddrOnStoreSelection: boolean = true;
    @field showStoreDescOnStoreSelection: boolean = false;
    @field sendOrderReceivedEmail: boolean = false;
    @field sendOrderReceivedSms: boolean = false;
    @component orderReceivedEmail: MessageData;
    @field sendOrderReadyEmail: boolean = false;
    @field sendOrderReadySms: boolean = false;
    @component orderReadyEmail: MessageData;
    @field expectedCountryCode: string = "61";
    @field desktopPillarboxing: boolean;
    @field showSelectedExtrasFirst: boolean;
    @field showTotalIncludedExtras: boolean;
    @field collapseExtras: boolean;
    @field compactExtrasThreshold: number;
    @field pointsName: string;
    @field memberNumberName: string;
    @field dietaryStatement: string;
    @field maxDaysOrderLater: number;
    @field selectionTimeInterval: number;
    @field showAsapSelectionTime: boolean;
    @field showTimeGrid: boolean;
    @field horizontalLogoImage: string;
    @field horizontalLogoHeightPx: number;
    @field verticalLogoImage: string;
    @field verticalLogoWidthPx: number;
    @component orderingImage: FedeltaImageData;
    @field orderingImageOpacity: number;
    @component loginImage: FedeltaImageData;
    @field loginImageOpacity: number;
    @field loginText1: string;
    @field loginText2: string;
    @field showRateApp: boolean;
    @collection(AppFeedbackData) feedbackCategories: AppFeedbackData[];
    @component appLoginOptions: AppLoginOptionsData;
    @component browserLoginOptions: AppLoginOptionsData;
    @field showNotes: boolean = true;
    @field promoPopupLimit: number = 3;
    @collection(AppThemePaletteEntryData) palette: AppThemePaletteEntryData[];
    @field firstTierButtonShape: ButtonShape = ButtonShape.ROUNDED;
    @field secondTierButtonShape: ButtonShape = ButtonShape.OVAL;
    @field firstTierHighProminenceButtonStyle: ButtonStyle = ButtonStyle.FLAT;
    @field secondTierHighProminenceButtonStyle: ButtonStyle = ButtonStyle.FLAT;
    @field firstTierLowProminenceButtonStyle: ButtonStyle = ButtonStyle.OUTLINE;
    @field secondTierLowProminenceButtonStyle: ButtonStyle = ButtonStyle.OUTLINE;
    @field integratedDelivery: boolean;
    @field startHourOfDay: number;
    @collection(AppCardThemeData) cardThemes: AppCardThemeData[];
    @collection(AppCustomTextData) customTexts: AppCustomTextData[];
    @collection(AppThemeSettingValueData) themeSettings: AppThemeSettingValueData[];
    @collection(AppSettingData) appSettings: AppSettingData[];
    @field promoteMemberLogin: boolean;
    @field promoteMemberLoginText1: string;
    @field promoteMemberLoginText2: string;
    @component promoteMemberLoginImage: FedeltaImageData;
    @field promoteMemberLoginImageAutoCrop: boolean;
    @field showMarketingOptIn: boolean = false;
    @field collectMarketingEmail: boolean = true;
    @field collectMarketingMobilePhone: boolean = false;
    @field collectMarketingPostCode: boolean = false;
    @field metaPixelId;
    @field googleTagId;
    @transientField horizontalLogoImageSvg: string;
    @transientField verticalLogoImageSvg: string;
}

@Injectable()
export class AppEntityModel extends EntityModel<AppData> {

    constructor(injector: Injector) {
        console.log("--- AppEntityModel");

        super(injector, AppData);
    }

    protected onAfterLoadEntity(appData: AppData): void {
        appData.horizontalLogoImageSvg = ImageUtils.base64ToSvg(appData.horizontalLogoImage);
        appData.verticalLogoImageSvg = ImageUtils.base64ToSvg(appData.verticalLogoImage);
    }
}



// WEBPACK FOOTER //
// ./src/app/orchestration/app-entity.model.ts